import { useMemo, useState } from "react";
import { FaArrowRight, FaAt, FaCheck, FaEnvelope, FaExclamation, FaFontAwesome, FaPhone, FaUser } from "react-icons/fa6";
import "./user-list-item.style.scss";
import "../form-inputs/form-inputs.style.scss";

// components
import CustomerProfile from "../user-profile-sidemodal/customer-profile.component";
import EmployeeProfile from "../user-profile-sidemodal/employee-profile.component";

// global functions and strings
import { isEmptyObject, convertTagToArray } from "../../global/function";
import type { Customer } from "../../queries/customer.queries";
import type { Employee, EmployeePermissions } from "../../queries/employee.queries";

type UserListItemProps =
	| {
			type: "employee";
			user: Partial<Employee>;
			currentPageNum?: number;
			empPermission?: EmployeePermissions;
	  }
	| {
			type: "customer";
			user: Partial<Customer>;
			currentPageNum?: number;
			empPermission?: EmployeePermissions;
	  };

function UserListItem({ user, type, currentPageNum, empPermission }: UserListItemProps) {
	const [isUserView, setIsUserView] = useState(false);
	const userData = useMemo(
		() => ({
			id:
				type === "employee"
					? user?.user_id != null
						? String(user.user_id)
						: undefined
					: user?.customer_id,
			img: type === "employee" ? user?.user_image : user?.customer_image,
			name:
				type === "employee"
					? `${user?.first_name} ${user?.last_name}`
					: user?.customer_name,
			mobile:
				type === "employee" ? user?.user_mobile : user?.customer_mobile,
			email:
				type === "employee" ? user?.user_email : user?.customer_email,
			verified: type === "employee" ? user?.user_verified : true,
			username:
				type === "employee"
					? user?.company_user?.company_login?.username ??
					  user?.logins?.[0]?.username ??
					  null
					: null,
			tags:
				type === "customer"
					? (convertTagToArray(user?.customer_tag ?? undefined) as string[])
					: [],
		}),
		[user, type],
	);


	return (
		<>
			{!isEmptyObject(userData) && (
				<div className="user-item-container">
					<div className="item-header-container">
						<div
							className="user-id"
							onClick={() => {
								type === "employee"
									? navigator.clipboard.writeText(
											userData?.id ?? "",
									  )
									: navigator.clipboard.writeText(
											userData?.id?.split("_")[1] ?? "",
									  );
							}}
						>
							# {userData?.id}
						</div>
						<div className="item-img-container">
							<div className="item_img">
								{userData.img ? (
									<img
										crossOrigin="anonymous"
										src={userData.img}
										alt="user"
									/>
								) : (
									<FaUser size="6em" />
								)}
							</div>

							<div
								className={`user-verified active ${
									userData.verified ? "active" : "inactive"
								}`}
							>
								{userData.verified ? (
									<FaCheck />
								) : (
									<FaExclamation />
								)}
							</div>
						</div>
					</div>

					<div className="item-details-container">
						{type === "employee" && (
							<div className="user-status-role">
								<div className="status-role-container">
									<p className="sr-label">Role</p>
									<h4 className="sr-value">
										{user?.company_user?.user_role}
									</h4>
								</div>
								<div className="separator"></div>
								<div className="status-role-container">
									<p className="sr-label">Status</p>
									<h4
										className={`sr-value ${user?.company_user?.company_user_status}`}
									>
										{user?.company_user?.company_user_status}
									</h4>
								</div>
							</div>
						)}
						<div>
							<h4 className="item-mainText cap-display">
								{userData?.name}
							</h4>
							<div className="details-divider" />
						</div>
						{type === "customer" && userData.tags.length > 0 && (
							<div className="tags-wrapper centered">
								{userData.tags.map((tag, index) => (
									<div className="tag-container" key={index}>
										<span className="tag-item">{tag}</span>
									</div>
								))}
							</div>
						)}
						<div style={{ flex: 1 }}>
							<div
								className="item-info copy"
								onClick={() => {
									navigator.clipboard.writeText(
										userData?.mobile ?? "",
									);
								}}
							>
								<div className="info-icon a">
									<FaPhone />
								</div>
								<div className="info-details">
									<p className="subText">Contact</p>
									<p className="mainText">
										{userData?.mobile}
									</p>
								</div>
							</div>
							{userData?.email && (
								<div
									className="item-info copy"
									onClick={() => {
										navigator.clipboard.writeText(
											userData?.email ?? "",
										);
									}}
								>
									<div className="info-icon b">
										<FaEnvelope />
									</div>
									<div className="info-details">
										<p className="subText">Email</p>
										<p className="mainText">
											{userData?.email}
										</p>
									</div>
								</div>
							)}
							{type === "employee" && userData?.username && (
								<div
									className="item-info copy"
									onClick={() => {
										navigator.clipboard.writeText(
											userData?.username ?? "",
										);
									}}
								>
									<div className="info-icon c">
										<FaAt />
									</div>
									<div className="info-details">
										<p className="subText">Username</p>
										<p className="mainText">
											{userData?.username}
										</p>
									</div>
								</div>
							)}

							{type === "customer" && (
								<div className="item-info">
									<div className="info-icon c">
										<FaFontAwesome />
									</div>
									<div className="info-details">
										<p className="subText">Customer Type</p>
										<p className="mainText capitalize">
											{user?.customer_type}
										</p>
									</div>
								</div>
							)}
						</div>
						<div
							className="item-action-btn"
							onClick={() => setIsUserView(true)}
						>
							Open Detailed View
							<FaArrowRight />
						</div>
					</div>

					{user?.isNew && <div className="new-flag">New</div>}
				</div>
			)}
			{type === "customer" && isUserView && (
				<CustomerProfile
					isCustomerVisible={isUserView}
					onCustomerModalClose={() => setIsUserView(false)}
					customer={user}
					currentPageNum={currentPageNum}
					onSelectCustomer={() => {}}
				/>
			)}
			{type === "employee" && isUserView && (
				<EmployeeProfile
					isEmployeeModalVisible={isUserView}
					onEmployeeModalClose={() => setIsUserView(false)}
					employee={user}
					empPermission={empPermission}
					currentPageNum={currentPageNum}
				/>
			)}
		</>
	);
}

export default UserListItem;
